import React, {
  ChangeEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { GlobalLocationNumber } from '../../common/types/models.ts';
import { CustomerLocationAndGLN } from '../../common/types/compoundModels.ts';
import api from '../../common/api/index.ts';
import { MdVerified } from 'react-icons/md';
import {
  CustomerLocationGlobalLocationNumberStatus,
  GLNDisposition,
} from '../../common/enums.ts';
import useCurrentCustomerLocationId from '../../hooks/useCurrentCustomerLocation.ts';
import endpoints from '../../common/endpoints.ts';
import { useQuery } from '@tanstack/react-query';

const MyGLNs = () => {
  const editSelectRef = useRef<HTMLSelectElement>(null);
  const modal = useRef<HTMLDivElement>(null);
  const { customerLocationId } = useCurrentCustomerLocationId();

  const initValues = (): {
    globalLocationNumber: string;
    disposition: GLNDisposition | '';
  } => ({
    globalLocationNumber: '',
    disposition: '',
  });

  const [values, setValues] = useState(initValues);
  useState<GlobalLocationNumber>();
  const [
    selectedCustomerLocationGlobalLocationNumber,
    setSelectedCustomerLocationGlobalLocationNumber,
  ] = useState<CustomerLocationAndGLN>();

  const getModal = () =>
    modal.current ? M.Modal.getInstance(modal.current) : null;

  const {
    data: customerLocationGlobalLocationNumberList,
    isPending: customerLocationGlobalLocationNumberListIsPending,
    refetch: refetchCustomerLocationGlobalLocationNumberList,
  } = useQuery({
    enabled: Boolean(customerLocationId),
    queryKey: [
      endpoints.customerLocationGlobalLocationNumber.read.many.by.userId(),
      customerLocationId,
    ],
    queryFn: api.customerLocationGlobalLocationNumber.read.many.by.userId,
  });

  useEffect(() => {
    if (modal.current) M.Modal.init(modal.current);
  }, []);

  const onChange = ({
    target: { id, value },
  }: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues(prev => ({ ...prev, [id]: value }));

  const editGln = (
    event: MouseEvent<HTMLAnchorElement>,
    clgln: CustomerLocationAndGLN
  ) => {
    event.preventDefault();
    setSelectedCustomerLocationGlobalLocationNumber(clgln);
    setValues(prev => ({
      ...prev,
      globalLocationNumber: clgln.globalLocationNumber.globalLocationNumber,
      disposition: clgln.globalLocationNumber.disposition,
    }));
    getModal()?.open();
    setTimeout(() => {
      M.updateTextFields();
      if (editSelectRef.current) M.FormSelect.init(editSelectRef.current);
    }, 50);
  };

  const cancel = (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    setSelectedCustomerLocationGlobalLocationNumber(undefined);
    getModal()?.close();
  };

  const createGln = async (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();

    if (customerLocationId) {
      const response =
        await api.customerLocationGlobalLocationNumber.create.one({
          globalLocationNumber: values.globalLocationNumber.trim(),
          customerLocationId: customerLocationId,
          disposition: values.disposition as GLNDisposition,
        });

      if (response) {
        refetchCustomerLocationGlobalLocationNumberList();
        getModal()?.close();
      }
    }
  };

  const updateGln = async (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    if (selectedCustomerLocationGlobalLocationNumber) {
      const response =
        await api.customerLocationGlobalLocationNumber.update.one({
          newGlobalLocationNumber: values.globalLocationNumber.trim(),
          disposition: values.disposition,
          customerLocationId:
            selectedCustomerLocationGlobalLocationNumber.customerLocation.id,
          globalLocationNumberId:
            selectedCustomerLocationGlobalLocationNumber.globalLocationNumber
              .id,
        });

      if (response) {
        refetchCustomerLocationGlobalLocationNumberList();
        getModal()?.close();
      }
    }
  };

  const addGln = (event: MouseEvent<HTMLAnchorElement>) => {
    event?.preventDefault();
    setSelectedCustomerLocationGlobalLocationNumber(undefined);
    setValues(prev => ({ ...prev, globalLocationNumber: '' }));
    getModal()?.open();
    setTimeout(() => {
      M.updateTextFields();
      if (editSelectRef.current) M.FormSelect.init(editSelectRef.current);
    }, 50);
  };

  const deleteGln = async (
    event: MouseEvent<HTMLAnchorElement>,
    clgln: CustomerLocationAndGLN
  ) => {
    event?.preventDefault();
    if (
      window.confirm(
        'Are you sure you want to delete this Global Location Number?'
      )
    ) {
      const {
        customerLocation: { id: customerLocationId },
        globalLocationNumber: { id: globalLocationNumberId },
      } = clgln;
      const response =
        await api.customerLocationGlobalLocationNumber.delete.one({
          customerLocationId,
          globalLocationNumberId,
        });

      if (response) {
        refetchCustomerLocationGlobalLocationNumberList();
      }
    }
  };

  const isClglnVerified = (clgln: CustomerLocationAndGLN) => {
    return (
      clgln.latestStatus === CustomerLocationGlobalLocationNumberStatus.CONFIRMED_ASSOCIATED ||
      clgln.latestStatus === CustomerLocationGlobalLocationNumberStatus.CONFIRMED_ROUTING
    );
  };

  return (
    <>
      <div className="row">
        <h5 className="center">Manage your pharmacy's GLNs</h5>
      </div>
      <div className="row">
        <a href="/" onClick={addGln}>
          Add Global Location Number
        </a>
        {customerLocationGlobalLocationNumberListIsPending ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className="preloader-wrapper active">
              <div className="spinner-layer spinner-blue-only">
                <div className="circle-clipper left">
                  <div className="circle" />
                </div>
                <div className="gap-patch">
                  <div className="circle" />
                </div>
                <div className="circle-clipper right">
                  <div className="circle" />
                </div>
              </div>
            </div>
            <div>
              <span>Loading . . .</span>
            </div>
          </div>
        ) : (
          <table className="highlight">
            <thead>
              <tr>
                <th>Global Location #</th>
                <th>Disposition</th>
                <th>Verified</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customerLocationGlobalLocationNumberList
                ?.filter(
                  ({ customerLocation: { id } }) =>
                    Number(id) === Number(customerLocationId)
                )
                .map(clgln => (
                  <tr key={clgln.globalLocationNumber.id}>
                    <td style={{ padding: '2px' }}>
                      {clgln.globalLocationNumber.globalLocationNumber}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {clgln.globalLocationNumber.disposition}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {isClglnVerified(clgln) ? (
                        <MdVerified
                          style={{ fontSize: '24px' }}
                          className="green-text text-darken-2"
                        />
                      ) : (
                        <span className="orange-text bold text-darken-2">
                          {clgln.latestStatus}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '2px' }}>
                      {isClglnVerified(clgln) ? null : (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-evenly',
                          }}
                        >
                          <a href="/" onClick={event => editGln(event, clgln)}>
                            Edit
                          </a>
                          <a
                            href="/"
                            onClick={event => deleteGln(event, clgln)}
                          >
                            Delete
                          </a>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
      <div ref={modal} id="gln-modal" className="modal">
        <div className="modal-content">
          <div className="row">
            <h6 className="center">
              {selectedCustomerLocationGlobalLocationNumber ? 'Update' : 'Add'}{' '}
              Global Location Number
            </h6>
          </div>
          <div className="row">
            <div className="input-field col s6 m5">
              <input
                id="globalLocationNumber"
                type="text"
                value={values.globalLocationNumber}
                onChange={onChange}
              />
              <label htmlFor="globalLocationNumber">Global Location #</label>
            </div>
            <div className="input-field col s6 m5">
              <select
                id="disposition"
                value={values.disposition}
                onChange={onChange}
                ref={editSelectRef}
              >
                <option value={''}></option>
                <option value={'SHIP TO'}>Ship To</option>
                <option value={'BILL TO'}>Bill To</option>
                <option value={'BOTH'}>Both</option>
              </select>
              <label htmlFor="disposition">Disposition</label>
            </div>
          </div>
          <div className="row">
            <div className="col s6 l3 offset-l6">
              <a
                href="/"
                className="btn-small blue white-text waves-effect waves-light col s12"
                onClick={
                  selectedCustomerLocationGlobalLocationNumber
                    ? updateGln
                    : createGln
                }
              >
                {selectedCustomerLocationGlobalLocationNumber
                  ? 'Update'
                  : 'Add'}{' '}
                GLN
              </a>
            </div>
            <div className="col s6 l3">
              <a
                href="/"
                className="btn-small blue white-text waves-effect waves-light col s12"
                onClick={cancel}
              >
                Cancel
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <p>
          Don't know your GLN? Click{' '}
          <a href="https://lookup.gs1us.org/location" target="_blank">
            here
          </a>{' '}
          to look it up.
        </p>
      </div>
    </>
  );
};

export default MyGLNs;
